
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react'

import {
  apiForgot,
  apiLogIn,
  apiReset,
  apiSignUp,
  clearSession,
  fetchMe,
  getToken,
  readCachedUser,
  saveSession,
  saveUser,
  signOutUser,
} from '../services/userAuth'

const UserContext = createContext(null)

export function UserProvider({ children }) {
  const [user, setUser] = useState(() =>
    getToken() ? readCachedUser() : null
  )

  const [authOpen, setAuthOpen] = useState(false)
  const [authMode, setAuthMode] = useState('login')

  // Restore the signed-in customer's profile when the site loads.
  useEffect(() => {
    if (!getToken()) {
      clearSession()
      return
    }

    let active = true

    fetchMe()
      .then((u) => {
        if (!active) return

        if (!u) {
          throw new Error('The server did not return a customer profile.')
        }

        saveUser(u)
        setUser(u)
      })
      .catch((err) => {
        if (!active) return

        if (err.status === 401 || err.status === 404) {
          clearSession()
          setUser(null)
        } else {
          console.error('[iPOINT] Could not restore customer session:', err)
        }
      })

    return () => {
      active = false
    }
  }, [])

  // Save a successful signup or login response.
  const startSession = useCallback((result) => {
    if (!result || typeof result !== 'object') {
      throw new Error(
        'The server returned an empty signup/login response. Please try again.'
      )
    }

    const { token, user: customer } = result

    if (!token || !customer) {
      console.error('[iPOINT] Invalid customer authentication response:', {
        hasToken: Boolean(token),
        hasUser: Boolean(customer),
      })

      throw new Error(
        'The server response is missing the login token or customer details.'
      )
    }

    saveSession(token, customer)
    setUser(customer)
    setAuthOpen(false)
  }, [])

  // Customer signup.
  const signUp = useCallback(
    async (form) => {
      const result = await apiSignUp(form)
      startSession(result)
      return result
    },
    [startSession]
  )

  // Customer login.
  const logIn = useCallback(
    async (email, password) => {
      const result = await apiLogIn(email, password)
      startSession(result)
      return result
    },
    [startSession]
  )

  // Request a password-reset link.
  const requestReset = useCallback(async (email) => {
    const result = await apiForgot(email)
    return result?.message || 'If the account exists, check your email for reset instructions.'
  }, [])

  // Set a new password using the reset token.
  const resetPassword = useCallback(async (token, password, confirm) => {
    const result = await apiReset(token, password, confirm)
    return result?.message || 'Your password has been reset.'
  }, [])

  // Sign out the current customer.
  const logout = useCallback(() => {
    signOutUser()
    setUser(null)
    setAuthOpen(false)
  }, [])

  // Open the customer authentication popup.
  const openAuth = useCallback((mode) => {
    setAuthMode(
      mode === 'signup' || mode === 'reset' || mode === 'forgot'
        ? mode
        : 'login'
    )
    setAuthOpen(true)
  }, [])

  const closeAuth = useCallback(() => {
    setAuthOpen(false)
  }, [])

  const value = useMemo(
    () => ({
      user,
      pending: null,
      authOpen,
      authMode,
      openAuth,
      closeAuth,
      signUp,
      logIn,
      requestReset,
      resetPassword,
      logout,
    }),
    [
      user,
      authOpen,
      authMode,
      openAuth,
      closeAuth,
      signUp,
      logIn,
      requestReset,
      resetPassword,
      logout,
    ]
  )

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  )
}

export const useUser = () => useContext(UserContext)
